// redirect-text-to-decision-ticket.ts
//
// F3 (spec §7.2/§7.3, D3): a `redirect`/`revert`... no — ONLY `redirect`
// whose payload carries free-text `text` (no `proposed_changes`) is
// materialized into a `kind:"decision"` ticket at the next tick boundary,
// instead of being silently dropped (the old bug this phase fixes:
// `RedirectDirectivePayload` never had a `text` field, so `applyRedirect-
// Directive` read nothing and the operator's instruction vanished).
//
// The decision worker's own output goes through the SAME done-path guard
// every other ticket uses (scope/acceptance, DAG validator phase 09) — this
// module opens NO new channel from directive payload straight to
// `proposed_changes` (D3: "KHÔNG mở đường proposed_changes qua payload
// redirect").
//
// Phase 08 empirical finding (e2e, not in the original phase spec): a
// ticket written by `writeTicketFile` alone at a tick BOUNDARY never
// survives past the very next dispatched tick — `runPreDispatchChecks`
// calls `recoverOwnedCheckout` (`git reset --hard` + `git clean -fd`) before
// EVERY dispatch, same class of bug `supervisor-bootstrap.ts` documents for
// `autopilot init`'s seeded tickets. F2's `applyRedirectDirective` has the
// identical latent gap (no producer exercised it yet, D4). This module
// closes it for its OWN new path by committing the ticket file via
// commit-owner.ts's `commitTicket` — the ONLY sanctioned commit path (spec:
// "Không có đường commit nào khác ngoài commit-owner.ts") — right after
// writing it, same tick, before any dispatch can run recoverOwnedCheckout.
import { randomUUID } from "node:crypto";
import { join } from "node:path";
import { commitTicket, type GitRunner } from "./commit-owner.js";
import type { MissionStore } from "./mission-store.js";
import type { PolicyAdapterInput, PolicyVerdict } from "./policy-adapter.js";
import type { Directive } from "./schemas/directive-schema.js";
import { type Ticket, TicketSchema } from "./schemas/ticket-schema.js";
import { ticketFileName } from "./ticket-file-name.js";
import { nextTicketId, writeTicketFile } from "./ticket-file-persistence.js";

const MAX_OUTCOME_BYTES = 4 * 1024;
const TRUNCATION_NOTE = "\n[...cắt bớt, vượt 4 KiB]";

/** Cắt `text` ở đúng 4 KiB (đo theo byte UTF-8), kèm ghi chú bị cắt. */
export function truncateRedirectText(text: string): string {
  const buf = Buffer.from(text, "utf8");
  if (buf.byteLength <= MAX_OUTCOME_BYTES) return text;
  const noteBytes = Buffer.byteLength(TRUNCATION_NOTE, "utf8");
  const budget = MAX_OUTCOME_BYTES - noteBytes;
  // Cắt theo byte rồi decode lại UTF-8 nới lỏng — an toàn vì đây chỉ là văn
  // bản hiển thị người đọc (outcome), không phải giá trị được so khớp lại.
  return `${buf.subarray(0, Math.max(0, budget)).toString("utf8")}${TRUNCATION_NOTE}`;
}

export interface MaterializeRedirectTextAsDecisionTicketInput {
  store: MissionStore;
  repoDir: string;
  directive: Directive;
  tickets: Ticket[];
  gitRunner: GitRunner;
  evaluatePolicy: (input: PolicyAdapterInput) => Promise<PolicyVerdict>;
  missionId: string;
  notify?: (line: string) => void;
}

export interface MaterializeRedirectTextAsDecisionTicketResult {
  /** `undefined` khi directive KHÔNG áp dụng module này (không có text, hoặc có proposed_changes). */
  ticketId?: string;
  /** Set khi git commit của ticket bị policy chặn — ticket file đã ghi nhưng CHƯA áp dụng (chưa đánh idempotency). */
  commitFailedReason?: string;
}

/**
 * Req 1 shape test: directive `redirect` có `payload.text` (không rỗng) và
 * KHÔNG có `proposed_changes`. Tách riêng khỏi hàm materialize để caller
 * (drain) phân biệt được "directive này KHÔNG thuộc nhánh F3" (đi tiếp qua
 * applyRedirectDirective, F2) với "directive NÀY thuộc F3 nhưng đã
 * materialize từ trước — replay sau crash" (materialize trả `{}` idempotent,
 * KHÔNG được rơi xuống F2 lần thứ hai).
 */
export function isRedirectTextOnlyDirective(directive: Directive): boolean {
  const payload = directive.payload as { text?: unknown; proposed_changes?: unknown };
  const text = typeof payload.text === "string" ? payload.text.trim() : "";
  return text.length > 0 && payload.proposed_changes === undefined;
}

/**
 * Req 1: directive `redirect` có `payload.text` (không rỗng) và KHÔNG có
 * `proposed_changes` ⇒ tạo 1 ticket `kind:"decision"` cho người xem "chỉ đạo
 * đã được xử lý thế nào" (thay vì nhúng thẳng vào prompt ticket kế — D3: mất
 * chỗ hiển thị). Idempotent theo `directive_id`: operation_id cố định nên
 * replay sau crash không tạo ticket thứ 2.
 */
export async function materializeRedirectTextAsDecisionTicket(
  input: MaterializeRedirectTextAsDecisionTicketInput,
): Promise<MaterializeRedirectTextAsDecisionTicketResult> {
  if (!isRedirectTextOnlyDirective(input.directive)) return {};
  const text = (input.directive.payload as { text: string }).text.trim();

  const operationId = `redirect-decision-${input.directive.directive_id}`;
  if (input.store.state.results[operationId] !== undefined) {
    // Đã materialize (crash-then-replay) — không tạo ticket thứ 2.
    return {};
  }

  const id = nextTicketId(input.tickets);
  const ticket = TicketSchema.parse({
    id,
    revision: 1,
    title: `Chỉ đạo ${input.directive.directive_id}`,
    kind: "decision",
    executor: "claude",
    stage: "DELIVERING",
    slice_id: "directive",
    depends_on: [],
    command_adapter: "decision-v1",
    plan_path: ".avatar/PLAN.md",
    phase_path: ".avatar/plans/bootstrap.md",
    allowed_paths: [],
    // Req 5 (bước 0): decision KHÔNG chạy qua EVIDENCE_REQUIRED_ADAPTER_IDS
    // (command-adapter-table.ts) nên evidence[] luôn được phép rỗng —
    // verifier không tra `verification_recipe` này vào verification-
    // profile.json cho adapter này (chỉ testcase/implement/test/fix mới bị
    // tra). Giá trị dưới đây theo đúng quy ước "<kind>-bootstrap" của
    // mission-initializer-tickets.ts's seedTicket — một chuỗi không rỗng,
    // không match recipe nào, đúng như "plan-bootstrap"/"survey-bootstrap".
    verification_recipe: "decision-bootstrap",
    required_capabilities: [],
    sources: [`directive:${input.directive.directive_id}`],
    outcome: truncateRedirectText(text),
  });

  await writeTicketFile(input.repoDir, ticket);

  // Commit the new ticket file NOW, same tick — otherwise the very next
  // dispatched tick's recoverOwnedCheckout (`git clean -fd`) wipes it before
  // the scheduler ever sees it (see header). commitTicket is the ONLY
  // sanctioned commit path; a policy_denied here is surfaced as a rejection
  // reason (caller journals directive_rejected) rather than silently
  // leaving an untracked file the next tick will destroy anyway.
  const commit = await commitTicket({
    store: input.store,
    repoDir: input.repoDir,
    gitRunner: input.gitRunner,
    evaluatePolicy: input.evaluatePolicy,
    manifestPaths: [join(".avatar", "tickets", ticketFileName(ticket))],
    missionId: input.missionId,
    runId: randomUUID(),
    ticketId: id,
    type: "chore",
    scope: "autopilot",
    subject: `redirect ${input.directive.directive_id} -> decision ticket`,
  });
  if (commit.status === "policy_denied") {
    return { commitFailedReason: commit.reason };
  }

  await input.store.commit([
    {
      event_id: `${operationId}-intent`,
      operation_id: operationId,
      delta: {
        kind: "operation_intent",
        operation_id: operationId,
        operation_kind: "redirect_text_to_decision_ticket",
        params: { directive_id: input.directive.directive_id, ticket_id: id },
      },
    },
    {
      event_id: `${operationId}-result`,
      operation_id: operationId,
      delta: { kind: "operation_result", operation_id: operationId, outcome: "applied" },
    },
    {
      event_id: randomUUID(),
      delta: { kind: "ticket_state_changed", ticket: id, status: "todo" },
    },
  ]);

  input.notify?.(`Redirect ${input.directive.directive_id} ⇒ ticket ${id} (decision)`);
  return { ticketId: id };
}
